import { createContext, useContext, useState, ReactNode } from "react";

export type Language = "en" | "ta";

const translations = {
  en: {
    // Navigation
    home: "Home",
    trips: "Trips",
    earnings: "Earnings",
    profile: "Profile",
    report: "Report",

    // Profile menu
    my_profile: "My Profile",
    notifications: "Notifications",
    theme: "Theme",
    language: "Language",
    settings: "Settings",
    sign_out: "Sign Out",
    dark_mode: "Dark mode",
    light_mode: "Light mode",
    manage_alerts: "Manage your alerts",
    app_preferences: "App preferences & support",
    log_out: "Log out of your account",
    english: "English",
    tamil: "Tamil",
    select_language: "Select your language",

    // Driver info
    driver_information: "Driver Information",
    driver_app: "Driver App",
    phone: "Phone",
    driver_id: "Driver ID",
    vehicle_reg: "Vehicle Reg. No.",
    vehicle_id: "Vehicle ID",
    online: "Online",
    offline: "Offline",

    // Trip status
    head_to_pickup: "Head to pickup",
    customer_waiting: "Customer waiting — Start trip",
    trip_in_progress: "Trip in Progress",

    // Actions
    accept: "Accept",
    decline: "Decline",
    reached: "I've Reached",
    start_trip: "Start Trip",
    end_trip: "End Trip",
    cancel_trip: "Cancel Trip",
    cancel: "Cancel",
    confirm: "Confirm",
    submit: "Submit",
    skip: "Skip",
    call: "Call",

    // Trip page
    trip_history: "Trip History",
    all: "All",
    completed: "Completed",
    cancelled: "Cancelled",
    no_trips: "No trips found",
    complete_first_trip: "Complete your first trip to see it here",
    loading_trips: "Loading trips…",

    // Earnings page
    net_earnings: "Net Earnings",
    total_collection: "Total Collection",
    today: "Today",
    week: "Week",
    month: "Month",
    this_week: "This Week",
    penalties: "Penalties",
    gross_earnings: "Gross earnings",
    net: "Net",
    commission: "commission",
    transaction_history: "Transaction History",
    no_transactions: "No transactions for",
    loading_earnings: "Loading earnings…",
    trip_earning: "Trip",
    bonus: "Bonus",
    penalty: "Penalty",
    deduction: "Deduction",

    // Performance page
    performance_report: "Performance Report",
    performance: "Performance",
    ratings: "Ratings",
    total: "Total",
    total_bookings: "Total Bookings",
    completed_trips: "Completed Trips",
    cancelled_trips: "Cancelled Trips",
    worked_hours: "Worked Hours",
    total_hours: "Total Hours",
    online_hours: "Online Hours",
    on_trip_hours: "On-Trip Hours",
    commission_rate: "Commission Rate",
    rating_breakdown: "Rating Breakdown",
    excellent: "Excellent",
    good: "Good",
    average: "Average",
    below_avg: "Below Avg",
    poor: "Poor",
    based_on: "Based on",
    ratings_label: "ratings",
    loading_report: "Loading report…",

    // Notifications
    no_notifications: "No notifications yet",
    notifications_empty_sub: "You'll see trip alerts and updates here",
    loading_notifications: "Loading notifications…",

    // Trip completion
    trip_completed: "Trip Completed!",
    final_fare: "Final Fare",
    cash_collect: "Cash • Collect from customer",
    received_cash: "Received Cash",
    rate_customer: "Rate Customer",
    submit_rating: "Submit Rating",
    distance: "Distance",
    duration: "Duration",
    payment: "Payment",

    // OTP & Opening KM
    enter_otp: "Enter OTP",
    enter_otp_sub: "Ask your customer for the 4-digit OTP",
    verify_otp: "Verify & Start Trip",
    enter_opening_km: "Enter Opening KM",
    opening_km_sub: "Record your odometer reading before starting",
    odometer_reading: "Odometer reading (km)",
    save_continue: "Save & Continue",

    // Cancel trip
    cancel_reason: "Cancel Reason",
    select_reason: "Select reason",
    cancel_confirm: "Are you sure you want to cancel?",

    // Availability
    go_online: "Go Online",
    go_offline: "Go Offline",
    today_trips: "Today's Trips",
    today_earnings: "Today's Earnings",

    // Common
    loading: "Loading…",
    no_data: "No data",
    safe_track: "Safe Track",
    driver_app_version: "Driver App v1.0.0",
    transaction: "transaction",
    transactions: "transactions",
    hrs: "hrs",
    km: "km",
    min: "min",
    cash: "Cash",
    vehicle_breakdown: "Vehicle Breakdown",
    personal_emergency: "Personal Emergency",
    customer_unreachable: "Customer Unreachable",
    other: "Other",
    not_logged_in: "You are not logged in",

    // Headers
    about: "About",
    about_sub: "Terms, privacy & more",
    terms_conditions: "Terms and Conditions",
    terms_read_sub: "Read our terms",
    privacy_policy: "Privacy Policy",
    privacy_data_sub: "How we handle your data",
    delete_account_header: "Delete Account",
    delete_account_sub: "Permanently remove your account",
    select_your_vehicle: "Select Your Vehicle",
    driver_registration: "Driver Registration",
    verify_mobile_sub: "Verify mobile number",
    verify_your_mobile: "Verify your mobile number",

    // Trips list
    mark_all_read: "Mark all read",
    all_trips: "All Trips",
    assigned: "Assigned",
    on_trip_status: "On Trip",
    reached_status: "Reached",
    pickup_label: "Pickup",
    drop_label: "Drop",
    trip_info: "Trip Info",
    fare_details: "Fare Details",
    customer_rating: "Customer Rating",
    cancellation_reason_label: "Cancellation Reason",
    booking_label: "Booking",
    trip_type_label: "Trip Type",
    ac_mode: "AC Mode",
    mode_label: "Mode",
    vehicle_type_label: "Vehicle Type",
    ac: "A/C",
    non_ac: "Non A/C",
    created_label: "Created",
    pickup_at: "Pickup At",
    drop_at: "Drop At",
    opening_km_label: "Opening KM",
    closing_km: "Closing KM",
    fare_before_discount: "Fare Before Discount",
    discount: "Discount",
    final_fare_label: "Final Fare",
    date_label: "Date",
    status_label: "Status",
    yesterday: "Yesterday",
    last_7_days: "Last 7 Days",
    this_month: "This Month",
    trip_suffix: "Trip",
    vehicle_label: "Vehicle",
    driver_label: "Driver",

    // Profile
    available_after_trip: "Available after the current trip ends.",

    // No internet
    no_internet_title: "No Internet Connection",
    no_internet_body: "Please check your mobile data or Wi-Fi connection. The app will resume automatically once you're back online.",

    // Required permissions
    required_permission_header: "Required Permission",
    perm_allow: "Allow",
    perm_granted: "Granted",
    perm_location_title: "Location",
    perm_location_body: "Find nearby bookings and run the trip meter — needs all-the-time access.",
    perm_battery_title: "Ignore Battery Optimisation",
    perm_battery_body: "Stay active in the background so trip alerts always reach you.",
    perm_developer_title: "Developer Option",
    perm_developer_body: "Keep this OFF to block fake-GPS tools from spoofing your location.",
    perm_fullscreen_title: "Full-Screen Trip Alerts",
    perm_fullscreen_body: "Allow trip offers to pop up over the lock screen so you never miss a ride.",
    perm_autostart_title: "Autostart",
    perm_autostart_body: "Allow the app to auto-start so trip alerts still arrive after the phone restarts or the app is closed.",
    bg_location_needed: "Background Location Needed",
    bg_location_body: "This app needs background location access to find nearby bookings and accurately calculate fares using the trip meter—even when closed.",
    ok: "OK",

    // Mandatory update
    update_required: "Update Required",
    update_required_body: "A newer version of the app is available. Please update to continue using the app.",
    action_needed: "Action needed",
    installed_label: "Installed",
    required_word: "Required",
    update_now: "Update Now",
    redirect_playstore: "You'll be redirected to the Play Store",

    // Delete account
    request_received: "Request Received",
    request_received_body: "Your account deletion request has been submitted. We will process it within 7 business days. You will receive a confirmation SMS once completed.",
    back_to_login: "Back to Login",
    this_action_irreversible: "This action is irreversible",
    this_action_irreversible_sub: "Once your account is deleted, it cannot be recovered.",
    what_happens_deletion: "What happens when you delete your account?",
    del_bullet_personal: "Your personal information (name, phone, photo) will be permanently removed.",
    del_bullet_history: "Your trip history and earnings records will be erased.",
    del_bullet_payouts: "Any pending payouts will be forfeited.",
    del_bullet_profile: "Your driver profile and associated vehicle assignments will be removed.",
    del_bullet_login: "You will no longer be able to log in with this phone number.",
    data_retention: "Data Retention",
    data_retention_body: "Some data may be retained for up to 90 days for legal, regulatory, or fraud prevention purposes. After the retention period, all remaining data will be permanently deleted.",
    enter_registered_phone: "Enter your registered phone number",
    phone_placeholder_10: "10-digit phone number",
    invalid_phone_10: "Please enter a valid 10-digit phone number",
    request_account_deletion: "Request Account Deletion",
    confirm_deletion_request: "Confirm Deletion Request",
    confirm_deletion_body_prefix: "Are you sure you want to request deletion of the driver account associated with",
    confirm_deletion_body_suffix: "? This action cannot be undone.",
    yes_delete_my_account: "Yes, Delete My Account",

    // Select vehicle
    pick_vehicle_prefix: "pick a vehicle to start",
    no_available_vehicles: "No available vehicles",
    no_available_vehicles_body: "All of your vendor's vehicles are currently paired. Contact your vendor to free one up.",
    refresh_label: "Refresh",
    pairing_ellipsis: "Pairing...",
    confirm_continue: "Confirm & Continue",
    sign_out_lower: "Sign out",
    vehicle_generic: "Vehicle",
    hi_prefix: "Hi",

    // Cancellation sheet
    request_cancellation: "Request cancellation",
    admin_will_review: "An admin will review your request. You cannot start or complete the trip until they respond.",
    reason_label: "Reason",
    details_optional: "Details (optional)",
    details_required_5: "Details (required, min 5 chars)",
    details_placeholder: "Add anything the admin should know…",
    min5_chars: "Please enter at least 5 characters.",
    back: "Back",
    submit_request: "Submit request",
    reason_vehicle_breakdown: "Vehicle breakdown",
    reason_personal_emergency: "Personal emergency",
    reason_customer_noshow: "Customer no-show",
    reason_unable_pickup: "Unable to reach pickup",
    reason_wrong_pickup: "Wrong pickup location",
    reason_other: "Other",
    no_internet_toast: "No internet connection",
    no_internet_toast_body: "Please reconnect to send your cancellation request.",
    cancellation_pending: "Cancellation request already pending",
    cancellation_pending_body: "Please wait for the admin to review your existing request.",
    couldnt_send_request: "Couldn't send request",
    cancellation_requested_title: "Cancellation requested",
    cancellation_requested_body: "Waiting for admin to review.",

    // Odometer scanner
    scan_odometer: "Scan Odometer",
    close_label: "Close",
    scan_failed: "Scan failed.",
    align_odometer: "Align odometer in the frame",
    hold_steady: "Hold steady, ensure good lighting, then tap Capture.",
    reading_odometer: "Reading odometer…",
    detected_reading: "Detected reading",
    try_clearer_photo: "Try a clearer, well-lit photo of the odometer.",
    capture: "Capture",
    processing_ellipsis: "Processing…",
    scan_again: "Scan Again",
    use_this: "Use this",

    // Home overlays
    current_trip: "Current Trip",
    next_trip: "Next Trip",
    enter_ride_otp: "Enter Ride OTP",
    opening_km_field: "Opening KM",
    scanned_reading: "Scanned reading",
    cancellation_requested_short: "Cancellation requested",
    admin_close_trip: "Admin will close this trip and calculate fare",
    gps_distance: "GPS Distance",
    gps_duration: "GPS Duration",
    fare_finalized: "Fare Finalized",
    exit_app: "Exit App",
    yes: "Yes",
    no: "No",
    exit_app_confirm: "Are you sure you want to exit the app?",

    // Fare summary / completion
    waiting_scratch_card: "Waiting for Scratch Card",
    fare_after_apply_skip: "Fare will appear after customer applies or skips",
    fare_summary: "Fare Summary",
    trip_fare: "Trip Fare",
    total_label: "Total",

    // About menu
    about_menu_terms_sub: "Read our terms",
    about_menu_privacy_sub: "How we handle your data",

    // Last updated
    last_updated_june_2026: "Last updated: June 2026",
    terms_intro: "Welcome to Safe Track Call Taxi. Please read these Terms and Conditions carefully before using the Driver App.",
    privacy_intro: "Your privacy is important to us. This Privacy Policy explains how Safe Track Call Taxi collects, uses, and protects your personal information as a driver.",
  },
  ta: {
    // Navigation
    home: "முகப்பு",
    trips: "பயணங்கள்",
    earnings: "வருமானம்",
    profile: "சுயவிவரம்",
    report: "அறிக்கை",

    // Profile menu
    my_profile: "என் சுயவிவரம்",
    notifications: "அறிவிப்புகள்",
    theme: "தீம்",
    language: "மொழி",
    settings: "அமைப்புகள்",
    sign_out: "வெளியேறு",
    dark_mode: "இருண்ட பயன்முறை",
    light_mode: "ஒளி பயன்முறை",
    manage_alerts: "உங்கள் எச்சரிக்கைகளை நிர்வகிக்கவும்",
    app_preferences: "செயலி விருப்பங்கள் & ஆதரவு",
    log_out: "உங்கள் கணக்கிலிருந்து வெளியேறு",
    english: "ஆங்கிலம்",
    tamil: "தமிழ்",
    select_language: "உங்கள் மொழியை தேர்ந்தெடுக்கவும்",

    // Driver info
    driver_information: "சாரதி தகவல்",
    driver_app: "சாரதி செயலி",
    phone: "தொலைபேசி",
    driver_id: "சாரதி ID",
    vehicle_reg: "வாகன பதிவு எண்",
    vehicle_id: "வாகன ID",
    online: "இணையில்",
    offline: "இணையற்று",

    // Trip status
    head_to_pickup: "பிக்கப் நோக்கி செல்லவும்",
    customer_waiting: "வாடிக்கையாளர் காத்திருக்கிறார் — பயணம் தொடங்கவும்",
    trip_in_progress: "பயணம் நடக்கிறது",

    // Actions
    accept: "ஏற்றுக்கொள்",
    decline: "மறு",
    reached: "சென்றடைந்தேன்",
    start_trip: "பயணம் தொடங்கு",
    end_trip: "பயணம் முடி",
    cancel_trip: "பயணம் ரத்து",
    cancel: "ரத்து",
    confirm: "உறுதிப்படுத்து",
    submit: "சமர்ப்பி",
    skip: "தவிர்",
    call: "அழை",

    // Trip page
    trip_history: "பயண வரலாறு",
    all: "அனைத்தும்",
    completed: "முடிந்தது",
    cancelled: "ரத்தானது",
    no_trips: "பயணங்கள் இல்லை",
    complete_first_trip: "உங்கள் முதல் பயணத்தை முடிக்கவும்",
    loading_trips: "பயணங்கள் ஏற்றுகிறது…",

    // Earnings page
    net_earnings: "நிகர வருமானம்",
    total_collection: "மொத்த வசூல்",
    today: "இன்று",
    week: "வாரம்",
    month: "மாதம்",
    this_week: "இந்த வாரம்",
    penalties: "அபராதங்கள்",
    gross_earnings: "மொத்த வருமானம்",
    net: "நிகர",
    commission: "கமிஷன்",
    transaction_history: "பரிவர்த்தனை வரலாறு",
    no_transactions: "பரிவர்த்தனைகள் இல்லை",
    loading_earnings: "வருமானம் ஏற்றுகிறது…",
    trip_earning: "பயணம்",
    bonus: "போனஸ்",
    penalty: "அபராதம்",
    deduction: "கழிவு",

    // Performance page
    performance_report: "செயல்திறன் அறிக்கை",
    performance: "செயல்திறன்",
    ratings: "மதிப்பீடுகள்",
    total: "மொத்தம்",
    total_bookings: "மொத்த முன்பதிவுகள்",
    completed_trips: "முடிந்த பயணங்கள்",
    cancelled_trips: "ரத்தான பயணங்கள்",
    worked_hours: "பணி நேரம்",
    total_hours: "மொத்த நேரம்",
    online_hours: "ஆன்லைன் நேரம்",
    on_trip_hours: "பயண நேரம்",
    commission_rate: "கமிஷன் விகிதம்",
    rating_breakdown: "மதிப்பீடு விவரம்",
    excellent: "சிறந்தது",
    good: "நல்லது",
    average: "சராசரி",
    below_avg: "சராசரிக்கு கீழ்",
    poor: "மோசம்",
    based_on: "அடிப்படையில்",
    ratings_label: "மதிப்பீடுகள்",
    loading_report: "அறிக்கை ஏற்றுகிறது…",

    // Notifications
    no_notifications: "இன்னும் அறிவிப்புகள் இல்லை",
    notifications_empty_sub: "பயண எச்சரிக்கைகள் இங்கே தெரியும்",
    loading_notifications: "அறிவிப்புகள் ஏற்றுகிறது…",

    // Trip completion
    trip_completed: "பயணம் முடிந்தது!",
    final_fare: "இறுதி கட்டணம்",
    cash_collect: "பணம் • வாடிக்கையாளரிடம் பெறவும்",
    received_cash: "பணம் பெற்றேன்",
    rate_customer: "வாடிக்கையாளரை மதிப்பிடுக",
    submit_rating: "மதிப்பீடு சமர்ப்பி",
    distance: "தூரம்",
    duration: "கால அளவு",
    payment: "பணம்",

    // OTP & Opening KM
    enter_otp: "OTP உள்ளிடவும்",
    enter_otp_sub: "வாடிக்கையாளரிடம் 4 இலக்க OTP கேளுங்கள்",
    verify_otp: "சரிபார்த்து பயணம் தொடங்கு",
    enter_opening_km: "தொடக்க கிலோமீட்டர் உள்ளிடவும்",
    opening_km_sub: "தொடங்குமுன் ஓடோமீட்டர் வாசிப்பை பதிவு செய்யவும்",
    odometer_reading: "ஓடோமீட்டர் வாசிப்பு (கி.மீ)",
    save_continue: "சேமி & தொடர்",

    // Cancel trip
    cancel_reason: "ரத்து காரணம்",
    select_reason: "காரணம் தேர்ந்தெடு",
    cancel_confirm: "நீங்கள் ரத்து செய்ய விரும்புகிறீர்களா?",

    // Availability
    go_online: "இணையில் செல்",
    go_offline: "இணையை விட்டு வெளியேறு",
    today_trips: "இன்றைய பயணங்கள்",
    today_earnings: "இன்றைய வருமானம்",

    // Common
    loading: "ஏற்றுகிறது…",
    no_data: "தரவு இல்லை",
    safe_track: "ஸ்விஃப்ட் டாக்சி",
    driver_app_version: "சாரதி செயலி v1.0.0",
    transaction: "பரிவர்த்தனை",
    transactions: "பரிவர்த்தனைகள்",
    hrs: "மணி",
    km: "கி.மீ",
    min: "நிமிடம்",
    cash: "பணம்",
    vehicle_breakdown: "வாகன கோளாறு",
    personal_emergency: "தனிப்பட்ட அவசரநிலை",
    customer_unreachable: "வாடிக்கையாளரை அடைய முடியவில்லை",
    other: "மற்றவை",
    not_logged_in: "நீங்கள் உள்நுழையவில்லை",

    // Headers
    about: "பற்றி",
    about_sub: "விதிகள், தனியுரிமை & மேலும்",
    terms_conditions: "விதிமுறைகள் மற்றும் நிபந்தனைகள்",
    terms_read_sub: "எங்கள் விதிகளை படிக்கவும்",
    privacy_policy: "தனியுரிமை கொள்கை",
    privacy_data_sub: "உங்கள் தரவை எப்படி கையாள்கிறோம்",
    delete_account_header: "கணக்கை நீக்கு",
    delete_account_sub: "உங்கள் கணக்கை நிரந்தரமாக நீக்கவும்",
    select_your_vehicle: "உங்கள் வாகனத்தை தேர்ந்தெடுக்கவும்",
    driver_registration: "சாரதி பதிவு",
    verify_mobile_sub: "மொபைல் எண்ணை சரிபார்க்கவும்",
    verify_your_mobile: "உங்கள் மொபைல் எண்ணை சரிபார்க்கவும்",

    // Trips list
    mark_all_read: "அனைத்தையும் படித்ததாக குறி",
    all_trips: "அனைத்து பயணங்கள்",
    assigned: "ஒதுக்கப்பட்டது",
    on_trip_status: "பயணத்தில்",
    reached_status: "சென்றடைந்தது",
    pickup_label: "பிக்கப்",
    drop_label: "இறக்கும் இடம்",
    trip_info: "பயண தகவல்",
    fare_details: "கட்டண விவரம்",
    customer_rating: "வாடிக்கையாளர் மதிப்பீடு",
    cancellation_reason_label: "ரத்து காரணம்",
    booking_label: "முன்பதிவு",
    trip_type_label: "பயண வகை",
    ac_mode: "ஏசி முறை",
    mode_label: "முறை",
    vehicle_type_label: "வாகன வகை",
    ac: "ஏசி",
    non_ac: "ஏசி இல்லை",
    created_label: "உருவாக்கியது",
    pickup_at: "பிக்கப் நேரம்",
    drop_at: "இறக்கும் நேரம்",
    opening_km_label: "தொடக்க கி.மீ",
    closing_km: "இறுதி கி.மீ",
    fare_before_discount: "தள்ளுபடிக்கு முன் கட்டணம்",
    discount: "தள்ளுபடி",
    final_fare_label: "இறுதி கட்டணம்",
    date_label: "தேதி",
    status_label: "நிலை",
    yesterday: "நேற்று",
    last_7_days: "கடந்த 7 நாட்கள்",
    this_month: "இந்த மாதம்",
    trip_suffix: "பயணம்",
    vehicle_label: "வாகனம்",
    driver_label: "சாரதி",

    // Profile
    available_after_trip: "தற்போதைய பயணம் முடிந்த பிறகு கிடைக்கும்.",

    // No internet
    no_internet_title: "இணைய இணைப்பு இல்லை",
    no_internet_body: "தயவுசெய்து உங்கள் மொபைல் தரவு அல்லது வைஃபை இணைப்பை சரிபார்க்கவும். இணைப்பு மீண்டும் வந்ததும் செயலி தானாக தொடரும்.",

    // Required permissions
    required_permission_header: "தேவையான அனுமதி",
    perm_allow: "அனுமதி",
    perm_granted: "வழங்கப்பட்டது",
    perm_location_title: "இருப்பிடம்",
    perm_location_body: "அருகிலுள்ள முன்பதிவுகளை கண்டறிய மற்றும் பயண மீட்டரை இயக்க — எல்லா நேரத்திலும் அணுகல் தேவை.",
    perm_battery_title: "பேட்டரி ஆப்டிமைசேஷனை புறக்கணி",
    perm_battery_body: "பயண எச்சரிக்கைகள் எப்போதும் உங்களை அடைய பின்னணியில் செயலில் இருங்கள்.",
    perm_developer_title: "டெவலப்பர் விருப்பம்",
    perm_developer_body: "போலி GPS கருவிகள் உங்கள் இருப்பிடத்தை மோசடி செய்வதை தடுக்க இதை ஆஃப் செய்யவும்.",
    perm_fullscreen_title: "முழுத்திரை பயண எச்சரிக்கைகள்",
    perm_fullscreen_body: "பயண சலுகைகள் பூட்டு திரையின் மேல் தோன்ற அனுமதிக்கவும், நீங்கள் ஒரு பயணத்தையும் தவறவிடாதீர்கள்.",
    perm_autostart_title: "தானாக தொடங்கு",
    perm_autostart_body: "தொலைபேசி மீண்டும் தொடங்கியதற்கு பிறகும் அல்லது செயலி மூடிய பிறகும் பயண எச்சரிக்கைகள் வர செயலியை தானாக தொடங்க அனுமதிக்கவும்.",
    bg_location_needed: "பின்னணி இருப்பிடம் தேவை",
    bg_location_body: "அருகிலுள்ள முன்பதிவுகளை கண்டறிய மற்றும் பயண மீட்டரை பயன்படுத்தி துல்லியமாக கட்டணத்தை கணக்கிட செயலிக்கு பின்னணி இருப்பிட அணுகல் தேவை — மூடப்பட்டிருக்கும் போது கூட.",
    ok: "சரி",

    // Mandatory update
    update_required: "புதுப்பிப்பு தேவை",
    update_required_body: "செயலியின் புதிய பதிப்பு கிடைக்கிறது. செயலியை தொடர்ந்து பயன்படுத்த புதுப்பிக்கவும்.",
    action_needed: "நடவடிக்கை தேவை",
    installed_label: "நிறுவப்பட்டது",
    required_word: "தேவை",
    update_now: "இப்போது புதுப்பி",
    redirect_playstore: "நீங்கள் ப்ளே ஸ்டோருக்கு திருப்பி விடப்படுவீர்கள்",

    // Delete account
    request_received: "கோரிக்கை பெறப்பட்டது",
    request_received_body: "உங்கள் கணக்கு நீக்க கோரிக்கை சமர்ப்பிக்கப்பட்டது. 7 வணிக நாட்களுக்குள் நாங்கள் செயலாக்குவோம். முடிந்ததும் உறுதிப்படுத்தல் SMS பெறுவீர்கள்.",
    back_to_login: "உள்நுழைவுக்கு திரும்பு",
    this_action_irreversible: "இந்த செயலை மீட்க முடியாது",
    this_action_irreversible_sub: "உங்கள் கணக்கு நீக்கப்பட்டால், மீட்க முடியாது.",
    what_happens_deletion: "உங்கள் கணக்கை நீக்கும் போது என்ன நடக்கும்?",
    del_bullet_personal: "உங்கள் தனிப்பட்ட தகவல் (பெயர், தொலைபேசி, புகைப்படம்) நிரந்தரமாக நீக்கப்படும்.",
    del_bullet_history: "உங்கள் பயண வரலாறு மற்றும் வருமான பதிவுகள் அழிக்கப்படும்.",
    del_bullet_payouts: "நிலுவையிலுள்ள பணம் அனைத்தும் இழக்கப்படும்.",
    del_bullet_profile: "உங்கள் சாரதி சுயவிவரம் மற்றும் தொடர்புடைய வாகன ஒதுக்கீடுகள் நீக்கப்படும்.",
    del_bullet_login: "இந்த தொலைபேசி எண்ணைக் கொண்டு நீங்கள் உள்நுழைய முடியாது.",
    data_retention: "தரவு தக்கவைப்பு",
    data_retention_body: "சட்ட, ஒழுங்குமுறை அல்லது மோசடி தடுப்பு நோக்கங்களுக்காக சில தரவுகள் 90 நாட்கள் வரை வைக்கப்படலாம். தக்கவைப்பு காலத்திற்குப் பிறகு, மீதமுள்ள அனைத்து தரவும் நிரந்தரமாக நீக்கப்படும்.",
    enter_registered_phone: "உங்கள் பதிவு செய்யப்பட்ட தொலைபேசி எண்ணை உள்ளிடவும்",
    phone_placeholder_10: "10 இலக்க தொலைபேசி எண்",
    invalid_phone_10: "செல்லுபடியாகும் 10 இலக்க தொலைபேசி எண்ணை உள்ளிடவும்",
    request_account_deletion: "கணக்கு நீக்கத்தை கோரவும்",
    confirm_deletion_request: "நீக்க கோரிக்கையை உறுதிப்படுத்து",
    confirm_deletion_body_prefix: "தொடர்புடைய சாரதி கணக்கை நீக்க வேண்டுமா",
    confirm_deletion_body_suffix: "? இந்த செயலை மீட்க முடியாது.",
    yes_delete_my_account: "ஆம், என் கணக்கை நீக்கு",

    // Select vehicle
    pick_vehicle_prefix: "தொடங்க ஒரு வாகனத்தை தேர்ந்தெடுக்கவும்",
    no_available_vehicles: "கிடைக்கக்கூடிய வாகனங்கள் இல்லை",
    no_available_vehicles_body: "உங்கள் விற்பனையாளரின் அனைத்து வாகனங்களும் தற்போது இணைக்கப்பட்டுள்ளன. ஒன்றை விடுவிக்க உங்கள் விற்பனையாளரை தொடர்பு கொள்ளவும்.",
    refresh_label: "புதுப்பி",
    pairing_ellipsis: "இணைக்கிறது...",
    confirm_continue: "உறுதிப்படுத்தி தொடர்",
    sign_out_lower: "வெளியேறு",
    vehicle_generic: "வாகனம்",
    hi_prefix: "வணக்கம்",

    // Cancellation sheet
    request_cancellation: "ரத்து கோரிக்கை",
    admin_will_review: "நிர்வாகி உங்கள் கோரிக்கையை மதிப்பாய்வு செய்வார். அவர்கள் பதிலளிக்கும் வரை நீங்கள் பயணத்தை தொடங்கவோ முடிக்கவோ முடியாது.",
    reason_label: "காரணம்",
    details_optional: "விவரங்கள் (விருப்பமானது)",
    details_required_5: "விவரங்கள் (தேவை, குறைந்தது 5 எழுத்துகள்)",
    details_placeholder: "நிர்வாகி அறிய வேண்டிய எதையும் சேர்க்கவும்…",
    min5_chars: "குறைந்தது 5 எழுத்துகள் உள்ளிடவும்.",
    back: "பின்",
    submit_request: "கோரிக்கையை சமர்ப்பி",
    reason_vehicle_breakdown: "வாகன கோளாறு",
    reason_personal_emergency: "தனிப்பட்ட அவசரநிலை",
    reason_customer_noshow: "வாடிக்கையாளர் வரவில்லை",
    reason_unable_pickup: "பிக்கப்பை அடைய முடியவில்லை",
    reason_wrong_pickup: "தவறான பிக்கப் இடம்",
    reason_other: "மற்றவை",
    no_internet_toast: "இணைய இணைப்பு இல்லை",
    no_internet_toast_body: "உங்கள் ரத்து கோரிக்கையை அனுப்ப மீண்டும் இணைக்கவும்.",
    cancellation_pending: "ரத்து கோரிக்கை ஏற்கனவே நிலுவையில்",
    cancellation_pending_body: "உங்கள் இருக்கும் கோரிக்கையை நிர்வாகி மதிப்பாய்வு செய்யும் வரை காத்திருக்கவும்.",
    couldnt_send_request: "கோரிக்கை அனுப்ப முடியவில்லை",
    cancellation_requested_title: "ரத்து கோரப்பட்டது",
    cancellation_requested_body: "நிர்வாகி மதிப்பாய்வுக்காக காத்திருக்கிறது.",

    // Odometer scanner
    scan_odometer: "ஓடோமீட்டரை ஸ்கேன் செய்",
    close_label: "மூடு",
    scan_failed: "ஸ்கேன் தோல்வி.",
    align_odometer: "ஓடோமீட்டரை சட்டகத்தில் சரிசெய்யவும்",
    hold_steady: "நிலையாக பிடித்து, நல்ல ஒளி இருப்பதை உறுதிசெய்து, பிடிக்கவும் என்பதை அழுத்தவும்.",
    reading_odometer: "ஓடோமீட்டர் படிக்கிறது…",
    detected_reading: "கண்டறியப்பட்ட வாசிப்பு",
    try_clearer_photo: "ஓடோமீட்டரின் தெளிவான, நல்ல ஒளியில் எடுத்த புகைப்படத்தை முயற்சிக்கவும்.",
    capture: "பிடி",
    processing_ellipsis: "செயலாக்குகிறது…",
    scan_again: "மீண்டும் ஸ்கேன்",
    use_this: "இதை பயன்படுத்து",

    // Home overlays
    current_trip: "தற்போதைய பயணம்",
    next_trip: "அடுத்த பயணம்",
    enter_ride_otp: "பயண OTP-ஐ உள்ளிடவும்",
    opening_km_field: "தொடக்க கி.மீ",
    scanned_reading: "ஸ்கேன் செய்யப்பட்ட வாசிப்பு",
    cancellation_requested_short: "ரத்து கோரப்பட்டது",
    admin_close_trip: "நிர்வாகி இந்த பயணத்தை மூடி கட்டணத்தை கணக்கிடுவார்",
    gps_distance: "GPS தூரம்",
    gps_duration: "GPS கால அளவு",
    fare_finalized: "கட்டணம் இறுதி செய்யப்பட்டது",
    exit_app: "செயலியை மூடு",
    yes: "ஆம்",
    no: "இல்லை",
    exit_app_confirm: "செயலியிலிருந்து வெளியேற விரும்புகிறீர்களா?",

    // Fare summary / completion
    waiting_scratch_card: "ஸ்கிராட்ச் கார்டுக்காக காத்திருக்கிறது",
    fare_after_apply_skip: "வாடிக்கையாளர் பயன்படுத்திய அல்லது தவிர்த்த பிறகு கட்டணம் தோன்றும்",
    fare_summary: "கட்டண சுருக்கம்",
    trip_fare: "பயண கட்டணம்",
    total_label: "மொத்தம்",

    // About menu
    about_menu_terms_sub: "எங்கள் விதிகளை படிக்கவும்",
    about_menu_privacy_sub: "உங்கள் தரவை எப்படி கையாள்கிறோம்",

    // Last updated
    last_updated_june_2026: "கடைசியாக புதுப்பிக்கப்பட்டது: ஜூன் 2026",
    terms_intro: "செய்ஃப் டிராக் கால் டாக்சிக்கு வரவேற்கிறோம். சாரதி செயலியை பயன்படுத்தும் முன் இந்த விதிமுறைகள் மற்றும் நிபந்தனைகளை கவனமாக படிக்கவும்.",
    privacy_intro: "உங்கள் தனியுரிமை எங்களுக்கு முக்கியம். சாரதியாக உங்கள் தனிப்பட்ட தகவலை செய்ஃப் டிராக் கால் டாக்சி எப்படி சேகரிக்கிறது, பயன்படுத்துகிறது, பாதுகாக்கிறது என்பதை இந்த தனியுரிமை கொள்கை விளக்குகிறது.",
  },
} as const;

type TranslationKey = keyof typeof translations.en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "app-language";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem(STORAGE_KEY) as Language) || "en";
    }
    return "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  const toggleLanguage = () => {
    setLanguage(language === "en" ? "ta" : "en");
  };

  const t = (key: TranslationKey): string => {
    return translations[language][key] ?? translations.en[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
